import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ShortlistProvider } from "@/context/ShortlistContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { ContentProvider } from "@/context/ContentContext";
import TrackerLoader from "@/components/TrackerLoader";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import ChildVoices from "@/pages/ChildVoices";
import ChildVoiceLanguage from "@/pages/ChildVoiceLanguage";
import TalentProfile, { VoiceLegacyRedirect } from "@/pages/TalentProfile";
import Casting from "@/pages/Casting";
import AIVoices from "@/pages/AIVoices";
import Studio from "@/pages/Studio";
import Work from "@/pages/Work";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Shortlist from "@/pages/Shortlist";
import Application from "@/pages/Application";
import Podcast from "@/pages/Podcast";
import JoinTalent from "@/pages/JoinTalent";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AuthCallback from "@/pages/AuthCallback";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTalents from "@/pages/admin/AdminTalents";
import AdminTalentEdit from "@/pages/admin/AdminTalentEdit";
import AdminLanguages from "@/pages/admin/AdminLanguages";
import AdminWork from "@/pages/admin/AdminWork";
import AdminClients from "@/pages/admin/AdminClients";
import AdminSite from "@/pages/admin/AdminSite";
import AdminContent from "@/pages/admin/AdminContent";
import AdminInbox from "@/pages/admin/AdminInbox";

import { LEGACY_REDIRECTS } from "@/config/legacyRedirects";

const withLayout = (el) => <Layout>{el}</Layout>;

// /languages/:slug (previous new-site URL) -> /child-voices/:slug
function LanguageRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/child-voices/${slug}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback before any route/auth check (race-condition safe)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={withLayout(<Home />)} />
      <Route path="/child-voices" element={withLayout(<ChildVoices />)} />
      <Route path="/child-voices/:slug" element={withLayout(<ChildVoiceLanguage />)} />
      <Route path="/voices/:lang/:name" element={withLayout(<TalentProfile />)} />
      <Route path="/voices/:id" element={withLayout(<VoiceLegacyRedirect />)} />

      {/* Directory relocation + legacy redirects (client-side fallback; true
          301s are handled at the hosting/CDN layer via _redirects/serve.json) */}
      <Route path="/voices" element={<Navigate to="/child-voices" replace />} />
      <Route path="/languages/:slug" element={<LanguageRedirect />} />
      {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}

      <Route path="/casting" element={withLayout(<Casting />)} />
      <Route path="/ai-voices" element={withLayout(<AIVoices />)} />
      <Route path="/studio" element={withLayout(<Studio />)} />
      <Route path="/work" element={withLayout(<Work />)} />
      <Route path="/about" element={withLayout(<About />)} />
      <Route path="/contact" element={withLayout(<Contact />)} />
      <Route path="/shortlist" element={withLayout(<Shortlist />)} />
      <Route path="/apply" element={withLayout(<Application />)} />
      <Route path="/podcast" element={withLayout(<Podcast />)} />
      <Route path="/join-our-talent" element={withLayout(<JoinTalent />)} />
      <Route path="/privacy" element={withLayout(<Legal kind="privacy" />)} />
      <Route path="/terms" element={withLayout(<Legal kind="terms" />)} />

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="talents" element={<AdminTalents />} />
        <Route path="talents/:id" element={<AdminTalentEdit />} />
        <Route path="languages" element={<AdminLanguages />} />
        <Route path="work" element={<AdminWork />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="site" element={<AdminSite />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="inbox" element={<AdminInbox />} />
      </Route>

      <Route path="*" element={withLayout(<NotFound />)} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ShortlistProvider>
        <PlayerProvider>
          <ContentProvider>
            <BrowserRouter>
              <TrackerLoader />
              <AppRouter />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </ContentProvider>
        </PlayerProvider>
      </ShortlistProvider>
    </AuthProvider>
  );
}

export default App;
